/**
 * InformeDetalle.tsx — read-only detail page for a created report.
 * Route: /informe/:id/detalle
 *
 * Shows: header summary + PDF embed (iframe) + action buttons.
 * Offline fallback: getReport() from IDB when getInforme() fails (mirrors InformeEditor pattern).
 *
 * PDF auto-generation (ponytail):
 *  - On mount, if pdfUrl is null AND estado ∈ {sincronizado, enviado} → auto-call generarPdfInforme once.
 *  - en_cola (offline): show "disponible cuando se sincronice" — no auto-gen, no spinner.
 *  - On error: show message + single "Reintentar" button (NOT auto-loop).
 *  - Once pdfUrl is set (persisted on server), never regenerates.
 *
 * Actions in this page: Ver PDF, Descargar PDF, Enviar (sincronizado only), Crear IS/AC.
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  Download,
  Eye,
  ExternalLink,
  GitFork,
  Loader2,
  MoreHorizontal,
  RefreshCw,
  Send,
  Share2,
  WifiOff,
} from "lucide-react";
import { toast } from "sonner";
import { TipoBadge } from "@/shared/ui/atoms/TipoBadge.js";
import { Button, buttonVariants } from "@/shared/ui/button.js";
import { BrandLogo } from "@/shared/ui/BrandLogo.js";
import { cn } from "@/shared/lib/utils.js";
import { friendlyMessage } from "@/shared/lib/errors.js";
import {
  getInforme,
  generarPdfInforme,
  changeEstadoInforme,
  listInformes,
  TIPO_LABEL,
} from "@/features/reports/index.js";
import { useAuth, useHomePath } from "@/features/auth/index.js";
import {
  getReport,
  getReportByServerId,
  getPdf,
  backupPdf,
  savePdf,
  pinPdf,
  unpinPdf,
  isPdfPinned,
} from "@/features/offline-sync/index.js";
import { crearContinuacionLocal } from "./crearContinuacionLocal.js";
import { previewNumero } from "../lib/numero.js";
import {
  buildInformeDetalle,
  isPendingNumero,
  nextFlowOptions,
  shouldAutoGeneratePdf,
  canEnviar,
  canCompartir,
  canVerFormulario,
  computePinState,
  type PinState,
  resolveDownloadSource,
} from "./informe-detalle-helpers.js";
import { buildMailtoEnviar } from "@/features/technician-panel/components/compartir-pdf.js";
import type { EstadoInforme, Informe, TipoInforme } from "../types.js";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog.js";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover.js";

const labelClass =
  "font-mono text-[0.625rem] font-medium uppercase tracking-[0.18em] text-muted-foreground";

const ESTADO_DOT: Record<EstadoInforme, string> = {
  borrador: "var(--estado-borrador)",
  en_cola: "var(--estado-en-cola)",
  sincronizado: "var(--estado-sincronizado)",
  enviado: "var(--estado-enviado)",
};

const ESTADO_BG: Record<EstadoInforme, string> = {
  borrador: "bg-[var(--estado-borrador-bg)] text-[var(--estado-borrador-text)]",
  en_cola: "bg-[var(--estado-en-cola-bg)] text-[var(--estado-en-cola-text)]",
  sincronizado: "bg-[var(--estado-sincronizado-bg)] text-[var(--estado-sincronizado-text)]",
  enviado: "bg-[var(--estado-enviado-bg)] text-[var(--estado-enviado-text)]",
};

function formatFecha(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("es-PE");
}

/** Can this report's estado show a PDF section at all? */
const PDF_ESTADOS: EstadoInforme[] = ["en_cola", "sincronizado", "enviado"];

/**
 * PDF preview: inline iframe on desktop (>= sm). Mobile browsers (Chrome Android,
 * Safari iOS) render `blob:`/PDF URLs inside an iframe as a black/empty box, so on
 * mobile we skip the iframe entirely and show a card that opens the PDF in the
 * native OS viewer instead.
 */
function PdfPreview({ url, title }: { url: string; title: string }) {
  return (
    <>
      <iframe src={url} title={title} className="hidden h-[60vh] w-full sm:block sm:h-[75vh]" />
      <div className="flex h-48 flex-col items-center justify-center gap-3 px-6 text-center sm:hidden">
        <ExternalLink className="size-6 text-muted-foreground" aria-hidden />
        <p className="text-sm text-muted-foreground">Vista previa disponible al abrir</p>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            buttonVariants({ variant: "outline", size: "sm" }),
            "w-full justify-center gap-1.5 font-mono text-[0.625rem] uppercase tracking-[0.1em]",
          )}
        >
          <ExternalLink className="size-3.5" aria-hidden />
          Abrir PDF
        </a>
      </div>
    </>
  );
}

export function InformeDetalle() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const homePath = useHomePath();

  const [informe, setInforme] = useState<Informe | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isLocalOnly, setIsLocalOnly] = useState(false);
  /** Tentative código shown while the real server número is still pending. */
  const [numeroPreview, setNumeroPreview] = useState<string | null>(null);

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [generandoPdf, setGenerandoPdf] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  /** Guard: only auto-generate once per mount. */
  const autoGenDone = useRef(false);
  /** Connectivity — drives the offline-friendly PDF messaging and reconnect refresh. */
  const [online, setOnline] = useState(navigator.onLine);
  /** Bumped on reconnect to re-fetch the report and pick up the server-generated PDF. */
  const [reloadKey, setReloadKey] = useState(0);
  const [descargandoPdf, setDescargandoPdf] = useState(false);
  /** Blob: URL of the offline-cached PDF, shown when the real server URL isn't reachable. */
  const [pdfObjectUrl, setPdfObjectUrl] = useState<string | null>(null);
  /** Blob: URL of OUR client-generated PDF (pdfmake via @marco/pdf) — the preview source. */
  const [ourPdfUrl, setOurPdfUrl] = useState<string | null>(null);
  /** True while our client PDF is being generated for the preview. */
  const [buildingPdf, setBuildingPdf] = useState(false);

  const [creandoHijo, setCreandoHijo] = useState<TipoInforme | null>(null);
  const [enviarModalOpen, setEnviarModalOpen] = useState(false);
  const [enviando, setEnviando] = useState(false);

  // ── Fase 2b: "Guardar para offline" pin state ──────────────────────────────
  /** The reportId used as the key in the `pdfs` IDB store (= serverId). */
  const [pdfCacheId, setPdfCacheId] = useState<string | null>(null);
  /** Whether the current PDF is pinned (immune to LRU eviction). */
  const [isPinned, setIsPinned] = useState(false);
  /** Whether a cached blob exists in IDB for this report. */
  const [hasCachedPdf, setHasCachedPdf] = useState(false);
  /** True while the PDF bytes are being downloaded and saved. */
  const [isDownloadingPin, setIsDownloadingPin] = useState(false);

  // ── Overflow "⋯ Más" menu state ───────────────────────────────────────────
  const [masMenuOpen, setMasMenuOpen] = useState(false);

  // Load informe — network first, IDB fallback for offline
  useEffect(() => {
    if (!id) return;
    let activo = true;
    setCargando(true);
    setError(null);
    autoGenDone.current = false;
    setPdfObjectUrl(null); // drop any stale offline blob; the old URL is revoked below

    async function cargar() {
      try {
        const data = await getInforme(id!);
        if (!activo) return;
        setInforme(data);
        setPdfUrl(data.pdfUrl);
        setIsLocalOnly(false);
        // Fase 2b: track the serverId as the PDF cache key.
        setPdfCacheId(data.id);
        // Proactively back up the PDF bytes for offline viewing (best-effort).
        if (data.pdfUrl) void backupPdf(data.id, data.pdfUrl);
      } catch {
        // Offline fallback: look up in IDB (by localId or serverId)
        const local = (await getReport(id!)) ?? (await getReportByServerId(id!));
        if (!activo) return;
        if (local) {
          const asInforme: Informe = {
            id: local.localId,
            tipo: local.tipo,
            numero: local.numero ?? "(pendiente)",
            cliente: local.cliente ?? "",
            clienteId: local.clienteId ?? null,
            estado: local.estado,
            autorId: "",
            parentId: local.parentLocalId ?? null,
            payload: local.payload as Record<string, unknown>,
            // Fase 2b: carry pdfUrl from IDB so the UI knows a PDF exists (cached during
            // a previous online session) — enables the "Guardar para offline" button offline.
            pdfUrl: local.pdfUrl ?? null,
            createdAt: local.updatedAt,
            updatedAt: local.updatedAt,
          };
          setInforme(asInforme);
          setPdfUrl(local.pdfUrl ?? null);
          setIsLocalOnly(true);
          // Fase 2b: track serverId for PDF cache key (null when report was never synced).
          setPdfCacheId(local.serverId ?? null);
          // Offline: serve the backed-up PDF if we cached it before (keyed by serverId).
          const cached = local.serverId ? await getPdf(local.serverId) : undefined;
          if (cached && activo) setPdfObjectUrl(URL.createObjectURL(cached));
        } else {
          setError("No se pudo cargar el informe.");
        }
      } finally {
        if (activo) setCargando(false);
      }
    }

    void cargar();
    return () => {
      activo = false;
    };
  }, [id, reloadKey]);

  // Connectivity tracking. On reconnect, re-fetch the report so the preview swaps to
  // the real server PDF (auto-generated/uploaded server-side) without a manual reload.
  useEffect(() => {
    const goOnline = () => {
      setOnline(true);
      setReloadKey((k) => k + 1);
    };
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  // Revoke the offline blob: URL when it is replaced or the page unmounts (no leak).
  useEffect(() => {
    return () => {
      if (pdfObjectUrl) URL.revokeObjectURL(pdfObjectUrl);
    };
  }, [pdfObjectUrl]);

  // Preview: always render OUR themed PDF on the device (online + offline), for
  // every tipo. The @marco/pdf browser adapter resolves offline `idb-blob:` image
  // placeholders via IndexedDB; Cloudinary URLs are fetched at gen time.
  // Heavy (pdfmake + Roboto vfs) → lazy-imported via client-pdf.ts dynamic import.
  useEffect(() => {
    if (!informe) {
      setOurPdfUrl(null);
      return;
    }
    let activo = true;
    let blobUrl: string | null = null;
    let imageUrls: string[] = [];
    setBuildingPdf(true);
    (async () => {
      const rec = (await getReport(informe.id)) ?? (await getReportByServerId(informe.id));
      const localId = rec?.localId ?? informe.id;
      const { buildClientPdf } = await import("./client-pdf.js");
      const { url, objectUrls } = await buildClientPdf(informe, localId);
      blobUrl = url;
      imageUrls = objectUrls;
      if (activo) setOurPdfUrl(url);
      else {
        URL.revokeObjectURL(url);
        objectUrls.forEach((u) => URL.revokeObjectURL(u));
      }
    })()
      .catch(() => {
        if (activo) setOurPdfUrl(null);
      })
      .finally(() => {
        if (activo) setBuildingPdf(false);
      });
    return () => {
      activo = false;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
      imageUrls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [informe]);

  // Q1: when the report has no real número yet, compute a tentative código from the
  // técnico's own report count (correlativo = count + 1), without hitting the create
  // endpoint. Only for the viewer's own reports — otherwise the count/iniciales would
  // belong to the wrong técnico. The real número is still assigned server-side on sync.
  useEffect(() => {
    if (!informe || !user) {
      setNumeroPreview(null);
      return;
    }
    if (!isPendingNumero(informe.numero)) {
      setNumeroPreview(null);
      return;
    }
    const esPropio = isLocalOnly || informe.autorId === user.id || informe.autorId === "";
    if (!esPropio) {
      setNumeroPreview(null);
      return;
    }

    let activo = true;
    listInformes()
      .then((todos) => {
        if (!activo) return;
        const correlativo = todos.filter((i) => i.autorId === user.id).length + 1;
        const nombre = informe.autorNombre || user.nombre;
        setNumeroPreview(
          previewNumero({
            tipo: informe.tipo,
            anio: new Date().getFullYear(),
            nombreTecnico: nombre,
            correlativo,
            clienteNombre: informe.cliente,
          }),
        );
      })
      .catch(() => {
        // Offline or list failed → no preview, fall back to the pending placeholder.
        if (activo) setNumeroPreview(null);
      });
    return () => {
      activo = false;
    };
  }, [informe, user, isLocalOnly]);

  // Fase 2b: check pin status and cached blob state when the PDF cache key is set.
  useEffect(() => {
    if (!pdfCacheId) return;
    let activo = true;
    void (async () => {
      const [pinned, cached] = await Promise.all([isPdfPinned(pdfCacheId), getPdf(pdfCacheId)]);
      if (!activo) return;
      setIsPinned(pinned);
      setHasCachedPdf(!!cached);
    })();
    return () => {
      activo = false;
    };
  }, [pdfCacheId]);

  // Auto-generate PDF once on mount when conditions are met.
  // Skip while offline — generation is server-side (Puppeteer). The offline-friendly
  // message is shown instead, and the reconnect effect re-runs this once back online.
  useEffect(() => {
    if (!informe) return;
    if (autoGenDone.current) return;
    if (!online) return;
    if (!shouldAutoGeneratePdf(informe.estado, pdfUrl)) return;

    autoGenDone.current = true;
    setPdfError(null);
    setGenerandoPdf(true);

    generarPdfInforme(informe.id)
      .then(({ pdfUrl: url }) => {
        setPdfUrl(url);
      })
      .catch((err) => {
        setPdfError(friendlyMessage(err));
      })
      .finally(() => {
        setGenerandoPdf(false);
      });
  }, [informe, pdfUrl, online]);

  /**
   * Fase 2b: download and pin the PDF for offline access.
   * Fetches from pdfUrl when online; falls back to the already-built client PDF blob.
   * Marks the record as pinned (immune to Fase 3 LRU eviction).
   */
  async function handleSaveOffline() {
    if (!pdfCacheId) return;
    setIsDownloadingPin(true);
    try {
      if (pdfUrl && online) {
        const saved = await backupPdf(pdfCacheId, pdfUrl);
        if (!saved) return;
      } else if (ourPdfUrl) {
        // Client-generated PDF is already a local blob URL — fetch its bytes.
        const res = await fetch(ourPdfUrl);
        const blob = await res.blob();
        await savePdf(pdfCacheId, blob);
      }
      await pinPdf(pdfCacheId);
      setIsPinned(true);
      setHasCachedPdf(true);
    } catch {
      // Best-effort: silently ignore failures (CORS, storage full, etc.)
    } finally {
      setIsDownloadingPin(false);
    }
  }

  /**
   * Fase 2b: unpin the PDF — makes it eligible for LRU eviction in Fase 3.
   * Does NOT delete the cached blob immediately.
   */
  async function handleUnpin() {
    if (!pdfCacheId) return;
    await unpinPdf(pdfCacheId);
    setIsPinned(false);
  }

  async function handleReintentar() {
    if (!informe) return;
    autoGenDone.current = false;
    setPdfError(null);
    setGenerandoPdf(true);
    try {
      const { pdfUrl: url } = await generarPdfInforme(informe.id);
      setPdfUrl(url);
      autoGenDone.current = true;
    } catch (err) {
      setPdfError(friendlyMessage(err));
    } finally {
      setGenerandoPdf(false);
    }
  }

  async function handleCrearHijo(childTipo: TipoInforme) {
    if (!informe) return;
    setCreandoHijo(childTipo);
    try {
      // ponytail: reuses crearContinuacionLocal from PR-B — works offline too.
      const childId = await crearContinuacionLocal(
        informe.id,
        childTipo as "IS" | "AC",
        informe.clienteId ?? undefined,
        informe.cliente,
      );
      navigate(`/informe/${childId}`);
    } catch (err) {
      toast.error(friendlyMessage(err));
      setCreandoHijo(null);
    }
  }

  /**
   * Shares the PDF via Web Share API (file attach on mobile) or falls back to mailto.
   * ponytail: same logic as the old compartirPdf in TechnicianPanel — moved here.
   * MANUAL gate: R2 bucket CORS must allow GET from app origin for blob fetch.
   */
  async function compartirPdf() {
    if (!informe || !pdfUrl) {
      toast.error("El PDF aún no está disponible.");
      return;
    }
    const mailtoUrl = buildMailtoEnviar({
      numero: informe.numero,
      cliente: informe.cliente,
      pdfUrl,
    });
    try {
      const res = await fetch(pdfUrl);
      if (!res.ok) throw new Error("pdf");
      const blob = await res.blob();
      const file = new File([blob], `${informe.numero}.pdf`, { type: "application/pdf" });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({
          files: [file],
        });
        return;
      }
      window.location.href = mailtoUrl;
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return;
      window.location.href = mailtoUrl;
    }
  }

  async function handleMarcarEnviado() {
    if (!informe) return;
    setEnviando(true);
    try {
      await changeEstadoInforme(informe.id, "enviado");
      setInforme((prev) => (prev ? { ...prev, estado: "enviado" } : prev));
      toast.success("Informe marcado como enviado");
      setEnviarModalOpen(false);
    } catch (err) {
      toast.error(friendlyMessage(err));
    } finally {
      setEnviando(false);
    }
  }

  /**
   * Download OUR client-generated pdfmake PDF directly (DWN-01).
   * The blob is already built for the on-device preview, so this works offline.
   * resolveDownloadSource governs which URL is used — always the client blob.
   */
  function descargarPdfOffline() {
    const downloadSrc = resolveDownloadSource(pdfUrl, ourPdfUrl);
    if (!downloadSrc) {
      toast.error("La vista previa todavía no está lista.");
      return;
    }
    setDescargandoPdf(true);
    try {
      const a = document.createElement("a");
      a.href = downloadSrc;
      a.download = `${informe?.numero ?? "informe"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } finally {
      setDescargandoPdf(false);
    }
  }

  // ── Render ───────────────────────────────────────────────────────────────

  if (cargando) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="size-6 animate-spin text-muted-foreground" aria-hidden />
      </div>
    );
  }

  if (error || !informe) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4">
        <p className="text-sm text-destructive">{error ?? "Informe no encontrado."}</p>
        <Button variant="outline" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="mr-1.5 size-3.5" aria-hidden />
          Volver
        </Button>
      </div>
    );
  }

  const display = buildInformeDetalle(informe, isLocalOnly, numeroPreview);
  const childOptions = nextFlowOptions(informe.tipo, informe.estado);
  const canShowPdf = PDF_ESTADOS.includes(informe.estado);
  const showEnviar = canEnviar(informe.estado);
  const compartirEnabled = canCompartir(pdfUrl);
  // DWN-01: resolveDownloadSource always returns the client blob URL; the server
  // Puppeteer URL is never used for download/open actions while the blob is ready.
  const downloadSrc = resolveDownloadSource(pdfUrl, ourPdfUrl);

  // Fase 2b: §3.2 — "Ver formulario" consult path requires connectivity.
  const verFormularioEnabled = canVerFormulario(online);

  // Fase 2b: compute "Guardar para offline" button state.
  const pinState: PinState = computePinState({
    hasPdfUrl: !!pdfUrl,
    hasClientPdf: !!ourPdfUrl,
    hasCachedBlob: hasCachedPdf,
    isPinned,
    isDownloading: isDownloadingPin,
    online,
  });

  // "Guardar para offline" is admin-only — gate by rol.
  const isAdmin = user?.rol === "admin";

  return (
    <div className="paper-surface relative min-h-screen overflow-hidden">
      <div aria-hidden className="paper-grid pointer-events-none absolute inset-0 opacity-50" />

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <header className="relative border-b border-border bg-card">
        <div aria-hidden className="absolute inset-x-0 top-0 h-1 bg-brand-accent" />
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <BrandLogo height={28} />
            <span aria-hidden className="hidden h-7 w-px bg-border sm:block" />
            <Link
              to={homePath}
              className={cn(labelClass, "hidden items-center gap-1 hover:text-foreground sm:flex")}
            >
              <ArrowLeft className="size-3" aria-hidden />
              Volver
            </Link>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(homePath)}
            className="gap-1.5 font-mono text-[0.625rem] uppercase tracking-[0.1em] sm:hidden"
          >
            <ArrowLeft className="size-3.5" aria-hidden />
            Volver
          </Button>
        </div>
      </header>

      <main className="relative mx-auto max-w-4xl space-y-6 px-6 py-8">
        {/* ── Summary header ────────────────────────────────────────────── */}
        <section className="rounded-lg border border-border bg-card p-6 space-y-5">
          {/* Top row: tipo + número + estado */}
          <div className="flex flex-wrap items-center gap-3">
            <TipoBadge tipo={informe.tipo} />
            <h1 className="font-display text-xl font-semibold tracking-tight text-foreground">
              {display.numero}
            </h1>
            {/* Estado pill — supports "Pendiente" for local-only en_cola */}
            <span
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[0.6875rem] font-medium",
                ESTADO_BG[informe.estado],
              )}
            >
              <span
                aria-hidden
                className="size-1.5 rounded-full"
                style={{ backgroundColor: ESTADO_DOT[informe.estado] }}
              />
              {display.estadoLabel}
            </span>
          </div>

          <p className={labelClass}>{TIPO_LABEL[informe.tipo]}</p>

          {/* Fields grid */}
          <dl className="grid grid-cols-2 gap-x-8 gap-y-4 sm:grid-cols-3">
            <div>
              <dt className={labelClass}>Cliente</dt>
              <dd className="mt-1 text-sm font-medium text-foreground">{display.cliente}</dd>
            </div>
            <div>
              <dt className={labelClass}>Técnico</dt>
              <dd className="mt-1 text-sm font-medium text-foreground">{display.tecnico}</dd>
            </div>
            <div>
              <dt className={labelClass}>Fecha Inicio</dt>
              <dd className="mt-1 text-sm font-medium text-foreground">
                {formatFecha(display.fechaInicio)}
              </dd>
            </div>
            <div>
              <dt className={labelClass}>Fecha Cierre</dt>
              <dd className="mt-1 text-sm font-medium text-foreground">
                {formatFecha(display.fechaCierre)}
              </dd>
            </div>
            <div>
              <dt className={labelClass}>Emisión</dt>
              <dd className="mt-1 text-sm font-medium text-foreground">
                {formatFecha(display.emision)}
              </dd>
            </div>
          </dl>
        </section>

        {/* ── Action buttons (RSP-01: stacks on mobile, row on sm+) ──────── */}
        <section className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
          {/* Descargar PDF — resolveDownloadSource (DWN-01) governs which URL is used:
              client pdfmake blob preferred; falls back to server pdfUrl. Works offline. */}
          {downloadSrc && (
            <Button
              variant="outline"
              size="sm"
              disabled={descargandoPdf}
              onClick={() => descargarPdfOffline()}
              className="justify-center gap-1.5 font-mono text-[0.625rem] uppercase tracking-[0.1em]"
            >
              {descargandoPdf ? (
                <Loader2 className="size-3.5 animate-spin" aria-hidden />
              ) : (
                <Download className="size-3.5" aria-hidden />
              )}
              Descargar PDF
            </Button>
          )}

          {/* Primary: Enviar — only from sincronizado, opens modal. */}
          {showEnviar && (
            <Button
              size="sm"
              onClick={() => setEnviarModalOpen(true)}
              className="gap-1.5 font-mono text-[0.625rem] uppercase tracking-[0.1em] bg-brand-accent text-brand-accent-foreground hover:bg-brand-accent/90"
            >
              <Send className="size-3.5" aria-hidden />
              Enviar
            </Button>
          )}

          {/* ⋯ Más — overflow menu (Radix Popover) */}
          <Popover open={masMenuOpen} onOpenChange={setMasMenuOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                aria-label="Más acciones"
                className="gap-1.5 font-mono text-[0.625rem] uppercase tracking-[0.1em]"
              >
                <MoreHorizontal className="size-3.5" aria-hidden />
                Más
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-60 p-1" align="end" role="menu">
              {/* Ver formulario — §3.2: requires connection */}
              {verFormularioEnabled ? (
                <Link
                  to={`/informe/${id}?ver=1`}
                  role="menuitem"
                  className="flex w-full flex-col rounded px-3 py-2 text-sm hover:bg-muted focus:outline-none focus-visible:bg-muted"
                  onClick={() => setMasMenuOpen(false)}
                >
                  <span className="flex items-center gap-2">
                    <Eye className="size-3.5 shrink-0" aria-hidden />
                    Ver formulario
                  </span>
                </Link>
              ) : (
                <div
                  role="menuitem"
                  aria-disabled="true"
                  className="flex w-full flex-col rounded px-3 py-2 text-sm opacity-50 cursor-not-allowed"
                >
                  <span className="flex items-center gap-2">
                    <Eye className="size-3.5 shrink-0" aria-hidden />
                    Ver formulario
                  </span>
                  <span className="mt-0.5 text-xs text-muted-foreground pl-5">
                    Disponible solo con conexión
                  </span>
                </div>
              )}

              {/* Guardar para offline — ADMIN only (§3.3). Pin state machine. */}
              {isAdmin && pinState !== "hidden" && (
                <>
                  {pinState === "saved" && (
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => void handleUnpin()}
                      className="flex w-full flex-col rounded px-3 py-2 text-sm text-green-700 hover:bg-muted focus:outline-none focus-visible:bg-muted text-left"
                    >
                      <span className="flex items-center gap-2">
                        <Check className="size-3.5 shrink-0" aria-hidden />
                        Disponible offline
                      </span>
                      <span className="mt-0.5 text-xs text-muted-foreground pl-5">
                        Tocar para quitar
                      </span>
                    </button>
                  )}
                  {pinState === "downloading" && (
                    <div
                      role="menuitem"
                      aria-disabled="true"
                      className="flex w-full items-center gap-2 rounded px-3 py-2 text-sm opacity-50 cursor-not-allowed"
                    >
                      <Loader2 className="size-3.5 animate-spin shrink-0" aria-hidden />
                      Guardando…
                    </div>
                  )}
                  {pinState === "offline-no-cache" && (
                    <div
                      role="menuitem"
                      aria-disabled="true"
                      className="flex w-full flex-col rounded px-3 py-2 text-sm opacity-50 cursor-not-allowed"
                    >
                      <span className="flex items-center gap-2">
                        <WifiOff className="size-3.5 shrink-0" aria-hidden />
                        Guardar para offline
                      </span>
                      <span className="mt-0.5 text-xs text-muted-foreground pl-5">
                        Requiere conexión para guardar
                      </span>
                    </div>
                  )}
                  {pinState === "not-saved" && (
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => void handleSaveOffline()}
                      className="flex w-full flex-col rounded px-3 py-2 text-sm hover:bg-muted focus:outline-none focus-visible:bg-muted text-left"
                    >
                      <span className="flex items-center gap-2">
                        <WifiOff className="size-3.5 shrink-0" aria-hidden />
                        Guardar para offline
                      </span>
                      <span className="mt-0.5 text-xs text-muted-foreground pl-5">
                        Lo guarda en tu equipo para usar sin conexión. No se borra solo.
                      </span>
                    </button>
                  )}
                </>
              )}

              {/* Crear siguiente flujo — IS / AC */}
              {childOptions.map((childTipo) => (
                <button
                  key={childTipo}
                  type="button"
                  role="menuitem"
                  disabled={creandoHijo !== null}
                  onClick={() => {
                    void handleCrearHijo(childTipo);
                    setMasMenuOpen(false);
                  }}
                  className="flex w-full items-center gap-2 rounded px-3 py-2 text-sm hover:bg-muted focus:outline-none focus-visible:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {creandoHijo === childTipo ? (
                    <Loader2 className="size-3.5 animate-spin shrink-0" aria-hidden />
                  ) : (
                    <GitFork className="size-3.5 shrink-0" aria-hidden />
                  )}
                  Crear {childTipo}
                </button>
              ))}
            </PopoverContent>
          </Popover>
        </section>

        {/* ── PDF embed ────────────────────────────────────────────────── */}
        {(canShowPdf || ourPdfUrl || buildingPdf) && (
          <section className="rounded-lg border border-border bg-card overflow-hidden">
            <div className="border-b border-border px-5 py-3">
              <p className={labelClass}>Vista previa del informe</p>
            </div>
            {ourPdfUrl ? (
              /* OUR pdfmake PDF (@marco/pdf) — generated on the device (online + offline).
                 Mobile shows the "Abrir PDF" card instead of the iframe (see PdfPreview). */
              <PdfPreview url={ourPdfUrl} title="Vista previa del informe" />
            ) : buildingPdf ? (
              <div className="flex h-48 flex-col items-center justify-center gap-3 text-center">
                <Loader2 className="size-6 animate-spin text-muted-foreground" aria-hidden />
                <p className="text-sm text-muted-foreground">Generando vista previa…</p>
              </div>
            ) : pdfUrl ? (
              <PdfPreview url={pdfUrl} title="Vista previa del PDF" />
            ) : pdfObjectUrl ? (
              /* Offline: showing the locally backed-up copy. */
              <div>
                <div className="border-b border-border bg-muted/40 px-5 py-2">
                  <p className="text-xs text-muted-foreground">
                    Copia guardada offline — el original se actualiza al recuperar señal.
                  </p>
                </div>
                <PdfPreview url={pdfObjectUrl} title="Vista previa del PDF (offline)" />
              </div>
            ) : informe.estado === "en_cola" ? (
              /* en_cola: offline, not yet on server — generation not possible yet. */
              <div className="flex h-48 flex-col items-center justify-center gap-2 text-center px-6">
                <p className="text-sm text-muted-foreground">
                  El PDF estará disponible cuando el informe se sincronice.
                </p>
              </div>
            ) : !online ? (
              /* Offline: generation is server-side. Refreshes automatically on reconnect. */
              <div className="flex h-48 flex-col items-center justify-center gap-2 text-center px-6">
                <WifiOff className="size-6 text-muted-foreground" aria-hidden />
                <p className="text-sm text-muted-foreground">
                  Sin conexión — el PDF se mostrará automáticamente al recuperar señal.
                </p>
              </div>
            ) : generandoPdf ? (
              /* Auto-generating PDF (sincronizado / enviado without pdfUrl). */
              <div className="flex h-48 flex-col items-center justify-center gap-3 text-center">
                <Loader2 className="size-6 animate-spin text-muted-foreground" aria-hidden />
                <p className="text-sm text-muted-foreground">Generando PDF…</p>
              </div>
            ) : pdfError ? (
              /* Auto-gen failed — show error + Reintentar (not auto-loop). */
              <div className="flex h-48 flex-col items-center justify-center gap-3 text-center px-6">
                <p className="text-sm text-destructive">{pdfError}</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void handleReintentar()}
                  className="gap-1.5 font-mono text-[0.625rem] uppercase tracking-[0.1em]"
                >
                  <RefreshCw className="size-3.5" aria-hidden />
                  Reintentar
                </Button>
              </div>
            ) : null}
          </section>
        )}
      </main>

      {/* ── Enviar modal — two independent actions ────────────────────────── */}
      <Dialog
        open={enviarModalOpen}
        onOpenChange={(open) => {
          if (!open && !enviando) setEnviarModalOpen(false);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Enviar informe</DialogTitle>
            <DialogDescription>
              {informe?.numero} — {informe?.cliente}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 px-6 py-6">
            {/* Action 1: Abrir correo con PDF adjunto */}
            <div className="rounded-lg border border-border p-4 space-y-3">
              <div>
                <p className="text-sm font-medium text-foreground">Abrir correo con PDF adjunto</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {compartirEnabled
                    ? "Abre tu app de correo con el PDF adjunto (en celular) o un enlace (en escritorio)."
                    : "Generá el PDF primero para poder compartirlo."}
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!compartirEnabled}
                onClick={() => void compartirPdf()}
                className="w-full justify-center gap-1.5 font-mono text-[0.625rem] uppercase tracking-[0.1em]"
              >
                <Share2 className="size-3.5" aria-hidden />
                Abrir correo con PDF
              </Button>
            </div>

            {/* Action 2: Marcar como enviado */}
            <div className="rounded-lg border border-border p-4 space-y-3">
              <div>
                <p className="text-sm font-medium text-foreground">Marcar como enviado</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Marca el informe como enviado. Es independiente de compartir.
                </p>
              </div>
              <Button
                type="button"
                size="sm"
                disabled={enviando}
                onClick={() => void handleMarcarEnviado()}
                className={cn(
                  "w-full justify-center gap-1.5 font-mono text-[0.625rem] uppercase tracking-[0.1em]",
                  "bg-brand-accent text-brand-accent-foreground hover:bg-brand-accent/90",
                )}
              >
                {enviando ? (
                  <Loader2 className="size-3.5 animate-spin" aria-hidden />
                ) : (
                  <Send className="size-3.5" aria-hidden />
                )}
                Marcar como enviado
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
